import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface IVirtualReferenceOptions {
  /**
   * 当锚点位于可滚动容器内时，传入滚动容器 ref 以在滚动时自动刷新位置。
   */
  scrollContainerRef?: RefObject<HTMLElement>;
  /**
   * 是否在窗口 resize 时自动刷新位置，默认 true。
   */
  listenWindowResize?: boolean;
  /**
   * 是否使用 ResizeObserver 监听锚点元素尺寸变化，默认 true（在不支持的环境下自动降级）。
   */
  observeAnchorResize?: boolean;
  /**
   * 设置锚点后是否在下一帧再刷新一次 rect，默认 true。
   * 用于覆盖“锚点样式切换/浮层挂载引发布局变化”导致首次测量不准的通用场景。
   */
  refreshAfterAnchorInRaf?: boolean;
}

export interface IVirtualReferenceElement {
  getBoundingClientRect: () => DOMRect;
  clientHeight: number;
  clientWidth: number;
}

interface IVirtualReferenceResult {
  /**
   * 传给 Popover/Bubble 的 referenceElement。
   * 该对象引用稳定，不会因切换锚点导致“热切换 referenceElement”产生闪动。
   */
  referenceElement: IVirtualReferenceElement;
  /**
   * 设置锚点元素（通常为点击的行 DOM）。可传 null 表示清空/关闭。
   */
  setAnchor: (el: HTMLElement | null) => void;
  /**
   * 主动刷新当前锚点的 rect（scroll/resize 已内置自动刷新，但某些重排场景可手动调用）。
   */
  refresh: () => void;
}

/**
 * 生成“虚拟 referenceElement”，可以用于 Popover/Bubble 等组件的 referenceElement 属性。
 * 可以参考 LegendColorSetting 组件内部的使用.
 * 目标：
 * - 始终维持单个 Popover/Bubble 实例；
 * - 切换锚点时不在 DOM 元素之间“热切换 referenceElement”，避免一帧错误定位/闪动；
 * - 可复用到任何依赖 referenceElement 的浮层组件。
 */
const useVirtualReferenceElement = (options?: IVirtualReferenceOptions): IVirtualReferenceResult => {
  const {
    scrollContainerRef,
    listenWindowResize = true,
    observeAnchorResize = true,
    refreshAfterAnchorInRaf = true,
  } = options || {};

  const anchorElRef = useRef<HTMLElement | null>(null);
  const anchorRectRef = useRef<DOMRect | null>(null);
  /**
   * 保存“延迟刷新”的 rAF id。
   * 目的：
   * - anchor 切换很快时，取消上一帧的 refresh，避免旧 anchor 的 rect 覆盖新 anchor；
   * - 组件卸载时清理，避免无意义的 refresh。
   */
  const rafIdRef = useRef<number | null>(null);

  // 仅用于触发重渲染，确保浮层在 rect 更新后重新计算位置。
  const [, forceUpdate] = useState(0);
  const [anchorVersion, setAnchorVersion] = useState(0);

  const isSameRect = useCallback((a: DOMRect | null, b: DOMRect | null): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
      a.top === b.top &&
      a.left === b.left &&
      a.right === b.right &&
      a.bottom === b.bottom &&
      a.width === b.width &&
      a.height === b.height
    );
  }, []);

  const measureAndUpdate = useCallback((): void => {
    const el = anchorElRef.current;
    const prevRect = anchorRectRef.current;

    if (!el) {
      if (prevRect) {
        anchorRectRef.current = null;
        forceUpdate((v) => v + 1);
      }
      return;
    }

    /**
     * 这里会触发同步布局测量（getBoundingClientRect）。
     * 因此对 scroll/resize 等高频来源，外部应优先走 rAF 合并（见 scheduleRefresh）。
     */
    const nextRect = el.getBoundingClientRect();
    if (isSameRect(prevRect, nextRect)) return;

    anchorRectRef.current = nextRect;
    forceUpdate((v) => v + 1);
  }, [isSameRect]);

  const cancelScheduledRefresh = useCallback((): void => {
    if (!rafIdRef.current) return;
    cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = null;
  }, []);

  const scheduleRefresh = useCallback((): void => {
    /**
     * rAF 合并：同一帧内无论触发多少次（scroll/resize/ResizeObserver），都只测量/更新一次。
     * 这能显著降低“滚动时每个事件都触发布局测量 + React 重渲染”的开销。
     */
    if (rafIdRef.current) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      measureAndUpdate();
    });
  }, [measureAndUpdate]);

  const refresh = useCallback((): void => {
    // 主动刷新：用于点击设置锚点等需要“立刻拿到 rect”以便浮层立即定位的场景。
    cancelScheduledRefresh();
    measureAndUpdate();
  }, [cancelScheduledRefresh, measureAndUpdate]);

  const setAnchor = useCallback(
    (el: HTMLElement | null): void => {
      anchorElRef.current = el;
      setAnchorVersion((v) => v + 1);
      refresh();

      if (!refreshAfterAnchorInRaf) return;

      /**
       * setAnchor 后做一次“下一帧 refresh”：
       * - 常见场景：点击行 → 行进入 selected 状态 / 浮层挂载 → 触发 reflow；
       * - 如果只在点击当下测一次 rect，拿到的可能是“变更前”的位置，导致浮层首次定位偏移（比如遮住锚点行）。
       * rAF 能确保在浏览器完成本帧布局后再测一次，得到更接近最终布局的 rect。
       */
      cancelScheduledRefresh();
      scheduleRefresh();
    },
    [cancelScheduledRefresh, refresh, refreshAfterAnchorInRaf, scheduleRefresh],
  );

  const referenceElement = useMemo<IVirtualReferenceElement>(() => {
    return {
      getBoundingClientRect: () => anchorRectRef.current ?? new DOMRect(0, 0, 0, 0),
      get clientHeight() {
        const rect = anchorRectRef.current;
        return rect ? Math.round(rect.height) : 0;
      },
      get clientWidth() {
        const rect = anchorRectRef.current;
        return rect ? Math.round(rect.width) : 0;
      },
    };
  }, []);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container) return;

    const handleScroll = (): void => {
      if (!anchorElRef.current) return;
      scheduleRefresh();
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [scheduleRefresh, scrollContainerRef]);

  useEffect(() => {
    if (!listenWindowResize) return;

    const handleResize = (): void => {
      if (!anchorElRef.current) return;
      scheduleRefresh();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [listenWindowResize, scheduleRefresh]);

  useEffect(() => {
    if (!observeAnchorResize) return;
    if (typeof ResizeObserver === 'undefined') return;

    const el = anchorElRef.current;
    if (!el) return;

    // 注意：锚点会变（点击不同 row），所以需要随 anchorVersion 重建 observer。
    const ro = new ResizeObserver(() => {
      scheduleRefresh();
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
    };
  }, [anchorVersion, observeAnchorResize, scheduleRefresh]);

  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    };
  }, []);

  return {
    referenceElement,
    setAnchor,
    refresh,
  };
};

export default useVirtualReferenceElement;

